class ProfessionalRegistrationPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-registration-dialog]');
    this.referralSelect = this.querySelector('[data-referral-select]');
    this.referralOther = this.querySelector('[data-referral-other]');
    this.otherInput = this.referralOther?.querySelector('input');
    this.registrationForm = this.querySelector('[data-registration-account-form]');
    this.storageKey = this.dataset.storageKey || 'professional-registration-dismissed';
    this.isDesignMode = this.dataset.designMode === 'true';

    if (!(this.dialog instanceof HTMLDialogElement)) return;

    this.querySelectorAll('[data-registration-close]').forEach((button) => {
      button.addEventListener('click', this.close);
    });

    document.addEventListener('click', this.handleOpenTrigger);
    document.addEventListener('click', this.handleCheckoutClick, true);
    document.addEventListener('submit', this.handleCheckoutSubmit, true);
    this.querySelector('[data-registration-sign-in]')?.addEventListener('click', this.rememberDismissal);
    this.referralSelect?.addEventListener('change', this.updateReferralOther);
    this.dialog.addEventListener('cancel', this.handleCancel);
    this.addEventListener('click', this.handleOverlayClick);
    this.addEventListener('keydown', this.handleKeydown);
    this.dialog.addEventListener('close', this.restorePageScroll);
    this.registrationForm?.addEventListener('submit', this.handleRegistrationSubmit);

    this.updateReferralOther();

    if (this.dataset.signedIn !== 'true' && this.dataset.autoOpen !== 'false' && !this.wasDismissed()) {
      window.setTimeout(this.open, 450);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleOpenTrigger);
    document.removeEventListener('click', this.handleCheckoutClick, true);
    document.removeEventListener('submit', this.handleCheckoutSubmit, true);
    this.registrationForm?.removeEventListener('submit', this.handleRegistrationSubmit);
    this.restorePageScroll();
  }

  handleCheckoutClick = (event) => {
    if (this.dataset.signedIn === 'true' || !(event.target instanceof Element)) return;
    const trigger = event.target.closest('[name="checkout"], a[href]');
    if (!trigger || trigger.disabled) return;
    if (trigger instanceof HTMLAnchorElement) {
      const destination = new URL(trigger.href, window.location.origin);
      if (destination.origin !== window.location.origin || !/\/checkout\/?$/.test(destination.pathname)) return;
    }
    this.requireRegistration(event, trigger);
  };

  handleCheckoutSubmit = (event) => {
    if (this.dataset.signedIn === 'true' || event.submitter?.name !== 'checkout') return;
    this.requireRegistration(event, event.submitter);
  };

  requireRegistration = async (event, trigger) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    // Release the cart drawer's modal and scroll lock before opening registration.
    const drawer = trigger.closest('cart-drawer-component');
    if (typeof drawer?.closeDialog === 'function') await drawer.closeDialog();
    this.open();
  };

  handleOpenTrigger = (event) => {
    if (this.dataset.signedIn === 'true') return;

    const trigger = event.target instanceof Element ? event.target.closest('[data-professional-registration-open]') : null;

    if (!(trigger instanceof HTMLElement)) return;

    event.preventDefault();
    const loginGateway = trigger.closest('professional-login-gateway');
    const loginDialog = loginGateway?.querySelector('[data-professional-login-dialog]');

    if (loginDialog instanceof HTMLDialogElement && loginDialog.open) {
      const previousBodyOverflow =
        typeof loginGateway.previousBodyOverflow === 'string' ? loginGateway.previousBodyOverflow : '';

      loginDialog.addEventListener(
        'close',
        () => {
          document.body.style.overflow = 'hidden';
        },
        { once: true }
      );

      this.open(previousBodyOverflow);
      loginDialog.close();
      document.body.style.overflow = 'hidden';
      return;
    }

    this.open();
  };

  wasDismissed() {
    if (this.isDesignMode) return false;

    try {
      return window.sessionStorage.getItem(this.storageKey) === 'true';
    } catch {
      return false;
    }
  }

  rememberDismissal = () => {
    if (this.isDesignMode) return;

    try {
      window.sessionStorage.setItem(this.storageKey, 'true');
    } catch {
      // Session storage can be unavailable in private browsing.
    }
  };

  handleRegistrationSubmit = (event) => {
    if (!(event.currentTarget instanceof HTMLFormElement)) return;

    event.preventDefault();
    const form = event.currentTarget;
    const emailInput = form.querySelector('[data-registration-account-email]');
    const loginUrl = form.dataset.loginUrl || form.action;

    if (!(emailInput instanceof HTMLInputElement) || !loginUrl) return;

    emailInput.value = emailInput.value.trim();

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    this.rememberDismissal();
    const destination = new URL(loginUrl, window.location.origin);
    destination.searchParams.set('login_hint', emailInput.value);
    window.location.assign(destination.href);
  };

  open = (previousBodyOverflow, registrationReview = false) => {
    if (this.dataset.signedIn === 'true' && !registrationReview) return;
    if (!(this.dialog instanceof HTMLDialogElement) || this.dialog.open) return;

    this.previousBodyOverflow =
      typeof previousBodyOverflow === 'string' ? previousBodyOverflow : document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.dialog.show();
    this.dataset.registrationOpen = 'true';
    window.requestAnimationFrame(() => {
      this.querySelector('[data-registration-close]')?.focus();
    });
  };

  close = () => {
    this.rememberDismissal();

    if (this.dialog instanceof HTMLDialogElement && this.dialog.open) {
      this.dialog.close();
    } else {
      this.restorePageScroll();
    }
  };

  handleCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  handleOverlayClick = (event) => {
    if (event.target === this) {
      this.close();
    }
  };

  handleKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  };

  restorePageScroll = () => {
    delete this.dataset.registrationOpen;
    document.body.style.overflow = this.previousBodyOverflow || '';
  };

  updateReferralOther = () => {
    const showOther = this.referralSelect instanceof HTMLSelectElement && this.referralSelect.value === 'Other';

    this.referralOther?.classList.toggle('hidden', !showOther);

    if (this.otherInput instanceof HTMLInputElement) {
      this.otherInput.required = showOther;

      if (!showOther) this.otherInput.value = '';
    }
  };

}

if (!customElements.get('professional-registration-popup')) {
  customElements.define('professional-registration-popup', ProfessionalRegistrationPopup);
}
